/**
 * Stock Report Page - Refactored with DataGrid and FilterPanel
 */
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { reportsAPI } from '../../lib/api';
import { useInstance } from '../../contexts/InstanceContext';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { DataGrid } from '../../components/ui/DataGrid';
import { FilterPanel } from '../../components/ui/FilterPanel';
import { Progress } from '../../components/ui/progress';
import { toast } from 'sonner';
import { PackageSearch, Download, AlertTriangle, PackageX, Package, RefreshCw, FileText, Loader2 } from 'lucide-react';

const PAGE_SIZE = 10;

export default function StockReport() {
  const { t } = useTranslation();
  const { formatCurrency } = useInstance();
  
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exportingPDF, setExportingPDF] = useState(false);

  // Filter state
  const [filters, setFilters] = useState({});
  const [filteredProducts, setFilteredProducts] = useState([]);

  const fetchReport = async () => {
    try {
      setLoading(true);
      const res = await reportsAPI.getStock();
      setReport(res.data);
    } catch (err) {
      toast.error('Erro ao carregar relatório');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [report, filters]);

  const applyFilters = useCallback(() => {
    if (!report?.all_products) {
      setFilteredProducts([]);
      return;
    }

    let result = [...report.all_products];

    if (filters.name) {
      result = result.filter(p => 
        p.name?.toLowerCase().includes(filters.name.toLowerCase())
      );
    }

    if (filters.status && filters.status !== 'all') {
      result = result.filter(p => {
        if (filters.status === 'out') return p.stock <= 0;
        if (filters.status === 'low') return p.stock > 0 && p.stock <= p.min_stock;
        if (filters.status === 'normal') return p.stock > p.min_stock;
        return true;
      });
    }

    setFilteredProducts(result.slice(0, PAGE_SIZE));
  }, [report, filters]);

  const handleSearch = () => {
    applyFilters();
  };

  const handleClearFilters = () => {
    setFilters({});
    if (report?.all_products) {
      setFilteredProducts(report.all_products.slice(0, PAGE_SIZE));
    }
  };

  const handleExportCSV = () => {
    if (!report?.all_products?.length) return;

    const headers = ['Produto', 'Estoque Atual', 'Estoque Mínimo', 'Status', 'Preço'];
    const rows = report.all_products.map(p => [
      p.name,
      p.stock,
      p.min_stock,
      p.stock <= 0 ? 'Sem estoque' : p.stock <= p.min_stock ? 'Baixo' : 'Normal',
      p.price,
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `relatorio-estoque-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Relatório CSV exportado!');
  };

  const handleExportPDF = async () => {
    try {
      setExportingPDF(true);
      const res = await reportsAPI.getStockPDF();
      
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio-estoque-${new Date().toISOString().split('T')[0]}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Relatório PDF exportado!');
    } catch (err) {
      toast.error('Erro ao exportar PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  const getStockStatus = (product) => {
    if (product.stock <= 0) return { label: 'Sem estoque', variant: 'destructive' };
    if (product.stock <= product.min_stock) return { label: 'Estoque baixo', variant: 'secondary' };
    return { label: 'Normal', variant: 'default' };
  };

  const getStockPercentage = (product) => {
    const maxStock = Math.max(product.min_stock * 3, 100);
    return Math.min((product.stock / maxStock) * 100, 100);
  };

  const filterDefinitions = [
    {
      key: 'name',
      label: 'Produto',
      type: 'text',
      placeholder: 'Buscar produto...',
    },
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'Todos',
      options: [
        { value: 'all', label: 'Todos' },
        { value: 'out', label: 'Sem estoque' },
        { value: 'low', label: 'Estoque baixo' },
        { value: 'normal', label: 'Normal' },
      ],
    },
  ];

  const columnDefs = useMemo(() => [
    {
      field: 'name',
      headerName: 'Produto',
      flex: 2,
      cellRenderer: (params) => (
        <div className="flex items-center gap-2">
          <Package className="h-4 w-4 text-muted-foreground" />
          <span className="font-medium">{params.value}</span>
        </div>
      ),
    },
    {
      field: 'stock',
      headerName: 'Estoque Atual',
      flex: 1.5,
      cellRenderer: (params) => (
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between">
            <span className="font-mono font-semibold">{params.value}</span>
            <span className="text-xs text-muted-foreground">/ {params.data.min_stock} mín</span>
          </div>
          <Progress value={getStockPercentage(params.data)} className="h-1" />
        </div>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      flex: 1,
      valueGetter: (params) => getStockStatus(params.data).label,
      cellRenderer: (params) => {
        const status = getStockStatus(params.data);
        return (
          <Badge variant={status.variant}>
            {params.data.stock <= 0 && <PackageX className="h-3 w-3 mr-1" />}
            {params.data.stock > 0 && params.data.stock <= params.data.min_stock && <AlertTriangle className="h-3 w-3 mr-1" />}
            {status.label}
          </Badge>
        );
      },
    },
    {
      field: 'price',
      headerName: 'Preço',
      flex: 1,
      cellRenderer: (params) => (
        <span className="font-mono">{formatCurrency(params.value)}</span>
      ),
    },
    {
      field: 'value',
      headerName: 'Valor em Estoque',
      flex: 1,
      valueGetter: (params) => params.data.stock * params.data.price,
      cellRenderer: (params) => (
        <span className="font-mono font-semibold">
          {formatCurrency(params.data.stock * params.data.price)}
        </span>
      ),
    },
  ], [formatCurrency]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="stock-report-page" data-testid="stock-report-page">
      <div className="page-header">
        <div className="page-header-title">
          <h1>
            <PackageSearch className="h-6 w-6" />
            Controle de Estoque
          </h1>
          <p>Acompanhe o estoque dos produtos</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchReport} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button variant="outline" onClick={handleExportCSV}>
            <Download className="h-4 w-4 mr-2" />
            CSV
          </Button>
          <Button onClick={handleExportPDF} disabled={exportingPDF}>
            {exportingPDF ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileText className="h-4 w-4 mr-2" />}
            PDF
          </Button>
        </div>
      </div>

      {report && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Total de Produtos</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{report.total_products || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-destructive flex items-center gap-1">
                  <PackageX className="h-4 w-4" />
                  Sem Estoque
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-destructive">{report.out_of_stock_count || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-orange-500 flex items-center gap-1">
                  <AlertTriangle className="h-4 w-4" />
                  Estoque Baixo
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-orange-500">{report.low_stock_count || 0}</div>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Valor Total</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold font-mono text-primary">
                  {formatCurrency(report.all_products?.reduce((sum, p) => sum + (p.stock * p.price), 0) || 0)}
                </div>
              </CardContent>
            </Card>
          </div>

          <FilterPanel
            filters={filterDefinitions}
            values={filters}
            onChange={setFilters}
            onSearch={handleSearch}
            onClear={handleClearFilters}
          />

          <DataGrid
            title="Controle de Estoque"
            columnDefs={columnDefs}
            rowData={filteredProducts}
            loading={loading}
            exportFileName="estoque"
            height="500px"
            getRowId={(params) => params.data.id}
          />
        </>
      )}
    </div>
  );
}
